import React, { Fragment, useMemo, useEffect, useRef, useState } from 'react';
import { StyledSidebar, StyledSidebarTop } from './styled';
import Navigation from '../../../../components/Navigation';
import List from '../../../../components/List';
import ListItem from '../../../../components/ListItem';
import NavigationItem from '../../../../components/NavigationItem';
import ListItemIcon from '../../../../components/ListItemIcon';
import Typography from '../../../../components/Typography';
import ListSubheader from '../../../../components/ListSubheader';
import { Col, Row } from 'react-styled-flexboxgrid';
import Avatar from '../../../../components/Avatar';
import useTheme from '../../../../hooks/useTheme';
import Box from '../../../../components/Box';
import { makeStyles } from '../../../../utils/styles';
import Notifications from '../Notifications';
import { getRoutesDashboard, getRoutesMain } from '../../../../routes';
import Icon from '../../../../components/Icon';
import { ROLES } from '../../../../utils/constans';
import administratorAvatar from '../../../../assets/images/administrator-avatar.png';
import { getImageUrl, getShortcutUsername, parseNotifications } from '../../../../utils/helpers';
import Messages from '../Messages';
import { useApi } from '../../../../hooks/useApi';
import { ApiNotifications } from '../../../../api/notifications';
import { useDispatch, useSelector } from "react-redux";
import { setIsReloadNotifications } from "../../../../redux/notifications/actions";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(css => ({
    typographyBox: css`
        margin-top: 35px;
    `,
    navigation: css`
        flex-grow: 1;
        flex-shrink: 1;
        overflow-y: auto;
        padding-bottom: 40px;
    `,
    list: css`
        height: 100%;
        display: flex;
        flex-direction: column;
    `,
    listItem: css`
        padding: 0;
    `,
    listSubheader: css`
        padding: 25px 40px;
    `,
    listLastItem: css`
        padding: 0;
        margin-top: auto;
    `
}));

const Sidebar = () => {
    const { t } = useTranslation()
    const theme = useTheme();
    const styles = useStyles();
    const dispatch = useDispatch();

    const limit = useMemo(() => 10, []);
    const [start, setStart] = useState(limit);

    const refNotificationsScroll = useRef(null);

    const user = useSelector(state => state.auth.user);
    const isReloadNotifications = useSelector(state => state.notifications?.isReloadNotifications);

    const [{ data: notificationsData, loading: notificationsLoading }, getNotifications] = useApi(ApiNotifications.get, { manual: true });
    const [{ data: countUnreadNotificationsData }, countUnreadNotifications] = useApi(ApiNotifications.countUnreadNotifications);
    const [, readNotification] = useApi(ApiNotifications.readNotification, { manual: true });
    const [, readAllNotifications] = useApi(ApiNotifications.readAllNotifications, { manual: true });

    const notifications = useMemo(
        () => parseNotifications(notificationsData),
        [notificationsData]
    );

    const isMoreNotifications = useMemo(
        () => !!(notifications && notifications.length > 0 && start <= notifications?.length),
        [start, notifications]
    );

    const isNoNotifications = useMemo(
        () => !!(notifications && notifications.length === 0),
        [notifications]
    );

    const isUnreadNotifications = useMemo(
        () => countUnreadNotificationsData !== undefined && countUnreadNotificationsData > 0,
        [countUnreadNotificationsData]
    );

    const isUnreadMessages = useMemo(
        () => countUnreadNotificationsData !== undefined && countUnreadNotificationsData > 0,
        [countUnreadNotificationsData]
    );

    // Get notifications if increase limit notifications
    useEffect(() => {
        getNotifications(0, start);
    }, [getNotifications, start])

    // Update notifications
    useEffect(() => {
        if (isReloadNotifications) {
            const async = async () => {
                const res = await getNotifications(0, start);
                if (res?.data) {
                    await countUnreadNotifications();
                    dispatch(setIsReloadNotifications(false));
                }
            }
            async();
        }
    }, [isReloadNotifications, getNotifications, countUnreadNotifications, start, dispatch]);

    const userFirstName = useMemo(() => user?.user?.first_name, [user]);
    const userLastName = useMemo(() => user?.user?.last_name, [user]);
    const username = useMemo(() => user?.user?.username, [user]);

    const role = useMemo(() => user?.user?.role?.type, [user]);
    const roleName = useMemo(() => user?.user?.role?.name, [user]);

    const routesDashboard = useMemo(() => getRoutesDashboard(role), [role]);
    const routesMain = useMemo(() => getRoutesMain(), []);

    const handleClickNotification = async notification => {
        if (notification?.id && notification?.is_unread) {
            const res = await readNotification(notification?.id);
            if (res?.data) {
                dispatch(setIsReloadNotifications(true));
            }
        }
    }

    const handleClickReadAllNotifications = async () => {
        const res = await readAllNotifications();
        if (res?.data) {
            dispatch(setIsReloadNotifications(true));
        }
    }

    const handleScrollNotifications = async (e) => {
        const top = e.target.scrollTop + e.target.offsetHeight;
        const bottom = e.target.scrollHeight - 10;
        if (top > bottom) {
            if (!notificationsLoading && isMoreNotifications) {
                setStart(prevStart => prevStart + limit);
            }
        }
    }

    return (
        <StyledSidebar>
            <StyledSidebarTop>
                <Row between="xs" middle="xs">
                    <Col>
                        <Avatar
                            alt={username}
                            src={role === ROLES.SUPERADMIN ? administratorAvatar : getImageUrl(user.avatar?.url)}
                            shortcut={getShortcutUsername(userFirstName, userLastName)}
                        />
                    </Col>
                    <Col>
                        <Row>
                            <Col>
                                <Notifications
                                    ref={refNotificationsScroll}
                                    showLoader={notificationsLoading && start === limit}
                                    loading={notificationsLoading}
                                    isMoreNotifications={isMoreNotifications}
                                    isNoNotifications={isNoNotifications}
                                    isUnreadNotifications={isUnreadNotifications}
                                    notifications={notifications}
                                    onScroll={handleScrollNotifications}
                                    onClickNotification={handleClickNotification}
                                    onClickReadAllNotifications={handleClickReadAllNotifications}
                                />
                            </Col>
                            {
                                role === ROLES.SUPERADMIN && (
                                    <Col>
                                        <Messages
                                            isUnreadMessages={isUnreadMessages}
                                        />
                                    </Col>
                                )
                            }
                        </Row>
                    </Col>
                </Row>
                <Box
                    customStyle={styles?.typographyBox}
                >
                    <Typography font={theme.fonts.mulishBold}>
                        {userFirstName} {userLastName} {(!userFirstName && !userLastName) && username}
                    </Typography>
                    <Typography color={theme.colors.dimGrey}>
                        {roleName}
                    </Typography>
                </Box>
            </StyledSidebarTop>
            <Navigation
                customStyle={styles?.navigation}
            >
                <List
                    customStyle={styles?.list}
                    noDivider
                >
                    {
                        routesDashboard && Object.values(routesDashboard).filter(item => !item.hideSidebar).map((route, index) => (
                            <Fragment key={route.path}>
                                {index === 3 && <ListSubheader customStyle={styles.listSubheader}>{t("ADMINISTRATOR")}</ListSubheader>}
                                <ListItem customStyle={styles.listItem}>
                                    <NavigationItem
                                        exact={route.exact}
                                        to={route.to}
                                    >
                                        <ListItemIcon>
                                            <Icon name={route.icon} />
                                        </ListItemIcon>
                                        <Typography>{t(route.name)}</Typography>
                                    </NavigationItem>
                                </ListItem>
                            </Fragment>
                        ))
                    }
                    <ListItem
                        customStyle={styles.listLastItem}
                    >
                        <NavigationItem
                            to={routesMain.logout.to}
                        >
                            <ListItemIcon>
                                <Icon name={routesMain.logout.icon} />
                            </ListItemIcon>
                            <Typography>{t(routesMain.logout.name)}</Typography>
                        </NavigationItem>
                    </ListItem>
                </List>
            </Navigation>
        </StyledSidebar>
    );
};

export default React.memo(Sidebar);
